import React, { useCallback } from "react";
import {
    getTransportModeGroupForItineraryLeg,
    isTransportModeInGroups,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { isMyCarLegToParkAndRide } from "../../../tools/parkAndRideOsmTags.tools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import ItineraryDetailsLegDetails from "./ItineraryDetailsLegDetails";
import ItineraryDetailsLegEnd from "./ItineraryDetailsLegEnd";
import ItineraryDetailsLegTitle from "./ItineraryDetailsLegTitle";
import ItineraryDetailsParkAndRideDestination from "./ItineraryDetailsParkAndRideDestination";
import ItineraryDetailsTransferRow from "./ItineraryDetailsTransferRow";
import { isTransitLegMode } from "./itineraryDetails.tools";

interface ItineraryDetailsLegProps {
    "isLegSelected": boolean;
    "leg":           ItineraryLegDTO;
    "nextLeg":       ItineraryLegDTO | undefined;
    "onLegClick":    ( leg: ItineraryLegDTO ) => void;
    "isDarkUi":      boolean;

    /** When false, removes right padding so legs align without the pricing segment column. */
    "hasPricingSegments"?: boolean;
}

const toTimestamp = ( value: string | undefined ): number | null => {
    if ( value == null ) {
        return null;
    }

    const parsed = new Date( value ).getTime();

    return Number.isNaN( parsed ) ? null : parsed;
};

const ItineraryDetailsLeg: React.FC<ItineraryDetailsLegProps> = ( {
    isLegSelected,
    leg,
    nextLeg,
    onLegClick,
    isDarkUi,
    hasPricingSegments = true,
} ) => {
    const modeGroup = getTransportModeGroupForItineraryLeg( leg );
    const shouldShowLegDetails = isTransitLegMode( leg.mode ) ||
      modeGroup === TransportModeGroup.RENTAL ||
      isTransportModeInGroups( leg.mode, [ TransportModeGroup.BLABLADAILY, TransportModeGroup.BLABLACAR ] );
    const shouldShowParkAndRideDestination = isMyCarLegToParkAndRide( leg );

    const shouldShowTransfer = isTransitLegMode( leg.mode ) && nextLeg != null && isTransitLegMode( nextLeg.mode );
    const transferFromMs = toTimestamp( leg.to?.time );
    const transferToMs = toTimestamp( nextLeg?.from?.time );
    const handleLegContainerClick = useCallback( ( event: React.MouseEvent<HTMLDivElement> ) => {
        // Keep segment pricing controls independent from leg selection.
        const clickTarget = event.target;
        if ( clickTarget instanceof Element && clickTarget.closest( "[data-leg-click-exempt='true']" ) !== null ) {
            return;
        }

        onLegClick( leg );
    }, [ leg, onLegClick ] );

    return (
        <div
            className={`m-1 mr-0 cursor-pointer rounded-lg p-2 transition-colors duration-200 ease-out hover:bg-ionic-primary/10 ${ hasPricingSegments ? "" : "pr-0" } ${ isLegSelected ? "bg-ionic-primary/10 ring-2 ring-ionic-primary/35" : "" }`}
            id={`itinerary-details-leg-${ leg.id }`}
            onClick={handleLegContainerClick}
        >
            {/* Main title row (mode icon, line pill, labels, departure time). */}
            <ItineraryDetailsLegTitle
                isDarkUi={isDarkUi}
                leg={leg}
                onLegClick={onLegClick}
            />
            {/* Additional leg metadata (duration, stops, arrival info) for rich modes. */}
            {shouldShowLegDetails
                ? (
                    <ItineraryDetailsLegDetails
                        isDarkUi={isDarkUi}
                        leg={leg}
                    />
                )
                : null}
            {/* Park-and-ride amenity / address block for mycar legs ending at a P+R. */}
            {shouldShowParkAndRideDestination
                ? (
                    <ItineraryDetailsParkAndRideDestination
                        isDarkUi={isDarkUi}
                        leg={leg}
                    />
                )
                : null}
            {/* Transfer row appears only between two transit-like legs. */}
            {shouldShowTransfer && transferFromMs != null && transferToMs != null
                ? (
                    <ItineraryDetailsTransferRow
                        fromMs={transferFromMs}
                        isDarkUi={isDarkUi}
                        legId={leg.id}
                        toMs={transferToMs}
                    />
                )
                : null}
            <ItineraryDetailsLegEnd
                isDarkUi={isDarkUi}
                leg={leg}
                nextLeg={nextLeg}
            />
        </div>
    );
};

export default ItineraryDetailsLeg;
